<template>
  <v-app :class="isZekerArbo ? 'zekerArbo' : ''">
    <template
      v-if="
        loginResult &&
        loginResult.redirectTo === LoginPhase.verwerkersovereenkomst
      "
    >
      <the-page-title :title="t('verwerkersovereenkomst')" />
      <the-title-bar :title="t('verwerkersovereenkomst')" />
      <base-page>
        <verwerkersovereenkomst-phase
          v-model:context="loginResult"
          v-model:login-phase="loginResult.redirectTo"
          @update:login-phase="onUpdateLoginPhase($event)"
        />
      </base-page>
    </template>

    <template v-else-if="isReady">
      <authenticated v-if="isLoggedIn" />
      <unauthenticated v-else />
      <the-apollo-error-dialog
        :is-open="dialogs.error.open"
        :cancelable="dialogs.error.cancelable"
        @reload-pressed="onReload"
        @cancel-pressed="dialogs.error.open = false"
      />
    </template>

    <template v-else>
      <div
        class="with-background-image fill-height d-flex justify-center align-center"
      >
        <v-progress-circular indeterminate color="primary" size="64" />
      </div>
    </template>

    <gebruikersvoorwaarden-dialog ref="gebruikersVoorwaardenDialogRef" />
    <base-error-dialog
      v-model:is-open="errorPopup.open"
      :known-error-key="errorPopup.knownErrorKey"
      :meta-data="errorPopup.metadata"
      :title="errorPopup.title"
      :message="errorPopup.message"
    />
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      location="bottom"
      timer="warning"
      :multi-line="true"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </v-app>
</template>

<script lang="ts" setup>
import {
  defineAsyncComponent,
  getCurrentInstance,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useTheme } from 'vuetify';
import { useI18n } from 'vue-i18n';
import { setErrorListener as setErrorListenerApollo } from '@/plugins/vueApollo';
import TheApolloErrorDialog from '@/common/components/zvdzo/TheApolloErrorDialog.vue';
import BaseErrorDialog from '@/common/components/zvdzo/BaseErrorDialog.vue';
import useLoginData, {
  setErrorListener as setErrorListenerLoginData,
  activeFeature,
  FeatureFlagId,
} from '@/common/composition/useLoginData';
import {
  awaitClosingOfErrorPopup,
  errorPopup,
} from '@/common/composition/useErrorPopup';
import GebruikersvoorwaardenDialog from '@/areas/login/views/index/components/GebruikersvoorwaardenDialog.vue';
import Unauthenticated from '@/Unauthenticated.vue';
import Authenticated from '@/Authenticated.vue';
import router from '@/plugins/router';
import getMatomo from '@/common/composition/getMatomo';
import initChatbot from './plugins/chatbot-vic';
import { isZekerArbo } from './common/netwerkbedrijven';
import { useApolloClient } from '@vue/apollo-composable';
import { gql } from '@apollo/client/core';
import sessionKeyExists from './common/sessionKeyExists';
import { LoginModel, LoginPhase } from './areas/login/views/index/types';
import { RouteName } from './router/route-names';
import { PortaalToegang } from './types';
import { useGetPortaalToegang } from '@/common/composition/useGetPortaalToegang';
import useModelPostWithErrorResponse from './common/composition/useModelPostWithErrorResponse';
import LOGIN_MUTATION from './areas/login/views/index/queries';
import { BaseDialogType } from './common/components/zvdzo/BaseDialog.types';
import { snackbar } from '@/common/composition/snackbar';
import useIsSsoEntra from '@/common/composition/useIsSsoEntra';
import useIsSsoSurf from './common/composition/useIsSsoSurf';
import { getMsalInstanceShared } from './authConfig';
import { useMsalAccountUsername } from '@/common/composition/useMsalAccountUsername';
import { AccountInfo } from '@azure/msal-browser';
import { scopeUri } from './common/msalScopeUri';

const { setMsalAccountUsername } = useMsalAccountUsername();

const VerwerkersovereenkomstPhase = defineAsyncComponent(
  () =>
    import('@/areas/login/views/index/components/VerwerkersovereenkomstPhase.vue'),
);

const isReady = ref(false);
const loginPhase = ref();
const loginResult = ref<LoginModel>();

const { client: apolloClient } = useApolloClient();
const { post: getPortaalToegangRequest } = useGetPortaalToegang();

const { post: postGebruikersvoorwaarden, returnData: approveReturnData } =
  useModelPostWithErrorResponse<LoginModel, void>(LOGIN_MUTATION, {
    variables: { path: 'Login/HasApproved' },
  });

const gebruikersVoorwaardenDialogRef = ref<BaseDialogType>();

async function validateMsalLoginOnBackend() {
  const GET_MSAL_LOGIN = gql`
    query msalLoginQuery {
      msalLogin @rest(method: "GET", path: "Login/MsalLogin") {
        algemeneVoorwaardenNietAkkoord
        popup {
          autopopup
          message
          modelState
          title
        }
        redirectTo
        redirectUrl
        showPopup
        succesvol
        verwerkersovereenkomstIsAkkoord
        verwerkersovereenkomstKlantteamTelefoonnummer
        verwerkersovereenkomstNaamOndertekenaar
        verwerkersovereenkomstOrganisatieNaam
      }
    }
  `;

  const result = await apolloClient.query({
    query: GET_MSAL_LOGIN,
    variables: {},
    fetchPolicy: 'no-cache', // optional: avoids caching
  });

  return result as { data: { msalLogin: LoginModel } };
}

const { t } = useI18n();

const dialogs = reactive({ error: { open: false, cancelable: true } });
setErrorListenerApollo(() => {
  dialogs.error = { open: true, cancelable: true };
});
setErrorListenerLoginData(() => {
  dialogs.error = { open: true, cancelable: false };
});

const { isLoggedIn, refreshLoginData, epdUrl, getLoginData, msalLogout } =
  useLoginData();

function getActiveMsalAccount() {
  const msalInstanceShared = getMsalInstanceShared();
  return (
    msalInstanceShared.getActiveAccount() ||
    msalInstanceShared.getAllAccounts()[0]
  );
}

function getUsernameByMsalAccount(account: AccountInfo) {
  const tokenClaimName = import.meta.env
    .VITE_MSAL_AUTH_USERNAME_TOKEN_CLAIM_NAME;
  return account.idTokenClaims
    ? (account.idTokenClaims[tokenClaimName] as string)
    : '';
}

async function getPortaalToegang(): Promise<PortaalToegang> {
  const account = getActiveMsalAccount();

  // Indien er geen MSAL tokens zijn, dan geen toegang indien SSO of
  // normale gebruikersnaam en wachtwoord login scherm indien geen SSO.
  if (!account) return PortaalToegang.Geen;

  account.username = getUsernameByMsalAccount(account);

  if (account.username) {
    setMsalAccountUsername(account.username);
  }

  if (!account.username) {
    throw new Error('Geen gebruikersnaam gevonden in B2C token');
  }

  const response = await getPortaalToegangRequest({
    input: { gebruikersnaam: account.username },
  });

  const portaalToegang =
    response?.data?.portaalToegang.portaalToegang || PortaalToegang.Geen;

  if (epdUrl.value && portaalToegang === PortaalToegang.AlleenPlanningsAgenda) {
    window.location.href = epdUrl.value;
    return portaalToegang; // We zijn geredirect
  }

  return portaalToegang;
}

async function msalLoginRedirect(): Promise<void> {
  const msalInstanceShared = getMsalInstanceShared();

  // MSAL neemt het over en herlaadt de pagina
  await msalInstanceShared.loginRedirect({ scopes: [scopeUri] });
}

async function handleBackendMsalLoginValidation() {
  // Valideer aan de backend of het e-mail in de token overeenkomt met een record in de 'Login' tabel en bepaal welke gedeelte van het inlog proces de frontend moet tonen.
  const result = await validateMsalLoginOnBackend();
  loginResult.value = result.data.msalLogin;

  // Toon 'SSO niet geautoriseerd' scherm, indien de gebruiker aan de backend niet geautoriseerd kon worden.
  if (!loginResult.value.succesvol) {
    router.replace({ name: RouteName.SsoNietGeautoriseerd });
    return;
  }

  // Indien we geen 'Algemene voorwaarden' scherm hoeven te tonen, zijn we klaar.
  if (!loginResult.value.algemeneVoorwaardenNietAkkoord) {
    await HandleLoginSuccess();
    return;
  }

  // Indien dialog connectie ref niet kan worden gemaakt, toon een foutmelding.
  if (!gebruikersVoorwaardenDialogRef.value) throw new Error('no dialog');

  // Toon het 'Algemene voorwaarden' scherm.
  const akkoord = await gebruikersVoorwaardenDialogRef.value.open();
  if (!akkoord) {
    isReady.value = true;
    msalLogout();
    return;
  }

  // Bewaar het antwoord van de gebruiker.
  await postGebruikersvoorwaarden();
  if (!approveReturnData.value) throw new Error('no return data found');
  loginResult.value = approveReturnData.value;

  // Indien het bewaren mislukt, toon een foutmelding.
  if (
    await checkAndShowLoginError(
      loginResult.value.popup,
      loginResult.value.showPopup,
    )
  ) {
    return;
  }

  // Ga door naar de 'Home' pagina.
  await HandleLoginSuccess();
}

async function HandleLoginSuccess() {
  if (loginResult.value?.succesvol) {
    loginPhase.value = loginResult.value.redirectTo;

    if (loginPhase.value === LoginPhase.loggedIn) {
      await refreshLoginData(); // Update data
      // Opmerking: haal portaalToegang uit je state/store hier als je die nodig hebt voor de routering
      await router.replace({ name: RouteName.HomeIndex });
    }
  }
}

async function checkAndShowLoginError(
  popup: LoginModel['popup'],
  showPopup: boolean | undefined,
): Promise<boolean> {
  if (showPopup && popup) {
    Object.assign(errorPopup, { ...popup, open: true });
    await awaitClosingOfErrorPopup();
    return true; // Er was een fout
  }
  return false; // Geen fout
}

const getMsalAccountUsernameFromToken = () => {
  const account = getActiveMsalAccount();
  if (!account) {
    return undefined;
  }
  return getUsernameByMsalAccount(account);
};

onMounted(async () => {
  const self = getCurrentInstance();

  // Matomo setup
  router.afterEach((to, from) => {
    if (to.name && to.name !== from.name) {
      getMatomo(self)?.trackPageView(String(to.name));
    }
  });

  // Chatbot setup
  watch(
    () => activeFeature.get(FeatureFlagId.ChatbotVIC),
    active => {
      if (active) initChatbot();
    },
    { immediate: true },
  );

  // Zorg voor laatste login data
  await getLoginData().catch(() => {
    isReady.value = true;
  });

  // De kern van de login flow
  await router.isReady();
  const route = router.currentRoute.value;

  // Als de route expliciet aangeeft dat inloggen niet nodig is, zijn we klaar
  if (
    route.meta.mustBeLoggedIn === false &&
    route.name !== RouteName.LoginIndex
  ) {
    isReady.value = true;
    return;
  }

  // Als we al ingelogd zijn, zijn we klaar
  if (isLoggedIn.value) {
    isReady.value = true;
    return;
  }

  // SSO
  // Voor SSO is de login flow anders, dan voor gewone Entra en Gebruikersnaam en Wachtwoord flow
  // Voor de SSO flow maakt het niet uit of het een 'SSO Entra' of 'SSO Surf' gebruiker is. Daar zit het verschil in de MSAL configuratie.
  const isSso = useIsSsoEntra() || useIsSsoSurf();
  if (isSso) {
    // Indien 'accesstoken' niet bestaat, toon 'SSO' inlogscherm via MSAL.
    if (!sessionKeyExists('accesstoken')) {
      await msalLoginRedirect();

      // Stop uitvoering, MSAL navigeert weg.
      isReady.value = true;
      return;
    }

    // Valideer 'accesstoken' en bepaal aan de backend of de gebruiker geautoriseerd is.
    // Indien geautoriseerd, ga dan verder met de rest van de login flow, zoals de 'Algemene voorwaarden'
    // of als de login flow klaar is de 'Home' pagina.
    await handleBackendMsalLoginValidation();

    // Op dit moment gebruikt alleen groot zakelijke markt SSO.
    // We hoeven hier dus niet te redirecten naar een ander portaal,
    // want SSO gebruikers moeten op dit portaal zijn.
    const username = getMsalAccountUsernameFromToken();
    if (username) setMsalAccountUsername(username);

    isReady.value = true;
    return;
  }

  // Voor de 'NIET' SSO flow, moeten we weten of de gebruiker op dit portaal moet zijn
  const portaalToegang = await getPortaalToegang();

  // Stop uitvoering, browser navigeert weg
  if (portaalToegang === PortaalToegang.AlleenPlanningsAgenda) return;

  // Via Login Form heb je via gebruikersnaam en B2C authenticatie een token gekregen.
  // Valideer deze token aan de backend en bepaal of de gebruiker geautoriseerd is.
  if (sessionKeyExists('accesstoken')) {
    await handleBackendMsalLoginValidation();

    const username = getMsalAccountUsernameFromToken();
    if (username) setMsalAccountUsername(username);

    isReady.value = true;
    return;
  }

  // In alle gevallen zijn we nu klaar met de login checks, toon de app
  isReady.value = true;
});

const theme = useTheme();
const darkModeMedia = window.matchMedia('(prefers-color-scheme: dark)');
theme.change(darkModeMedia.matches ? 'dark' : 'light');

if (isZekerArbo.value) {
  theme.change(darkModeMedia.matches ? 'darkZekerArbo' : 'lightZekerArbo');
}

darkModeMedia.addEventListener('change', ({ matches }) => {
  if (
    !activeFeature.get(FeatureFlagId.DarkMode) &&
    import.meta.env.MODE !== 'development'
  ) {
    return;
  }

  theme.change(matches ? 'dark' : 'light');
  if (isZekerArbo.value) {
    theme.change(matches ? 'darkZekerArbo' : 'lightZekerArbo');
  }
});

const onReload = () => {
  window.location.reload();
};

const onUpdateLoginPhase = async (newLoginPhase: LoginPhase) => {
  loginPhase.value = newLoginPhase;

  if (newLoginPhase === LoginPhase.loggedIn) {
    // Haal nieuwe login data op om zeker te zijn dat portaalToegang up to date is
    await refreshLoginData();
    await router.replace({ name: RouteName.HomeIndex });
  }
};
</script>

<i18n locale="nl" lang="json">
{
  "verwerkersovereenkomst": "Verwerkersovereenkomst"
}
</i18n>
<i18n locale="en" lang="json">
{
  "verwerkersovereenkomst": "Verwerkersovereenkomst"
}
</i18n>
